import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  FileNameTypes,
  FileUploadStatus,
  GetAssemblyAttachmentsDocument,
  GetAssemblyDetailsDocument,
  useUpdateFileStatusMutation,
  useUploadFilesMutation,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useGetPdfImages } from '@/hooks/useGetPdfImages';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useSetAtom } from 'jotai';
import { isBulkReferenceDrawingsOpen } from '@/components/Analysis/state/atoms';
import { abyssColors } from '@/theme/colors';
import { useWizard } from '../context/WizardContext';

export const Review = () => {
  const { fileMappings, goToPreviousStep } = useWizard();
  const structureId = useGetStructureId();
  const { getImages } = useGetPdfImages();
  const [uploadFiles] = useUploadFilesMutation();
  const [updateFileStatus] = useUpdateFileStatusMutation();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const setIsOpen = useSetAtom(isBulkReferenceDrawingsOpen);

  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleUpload = async () => {
    setIsUploading(true);
    setProgress(0);

    const total = fileMappings.length;
    let completed = 0;
    let hasError = false;

    for (const mapping of fileMappings) {
      if (!mapping.assemblyId || !mapping.tagName) continue;

      try {
        const images = await getImages(mapping.file);
        const timestamp = Date.now();
        const baseName = mapping.file.name.replace(/\.[^/.]+$/, '');

        const fileInputs = images.map((image, index) => ({
          uuid: `${mapping.assemblyId}-${timestamp}-${index}`,
          name: `${mapping.tagName}_${timestamp}_${baseName}_page${index + 1}.png`,
          mimeType: 'image/png',
          size: image.size,
        }));

        const { data } = await uploadFiles({
          variables: {
            input: {
              structureId,
              collectionName: 'assembly',
              referenceId: mapping.assemblyId,
              fileNameType: FileNameTypes.ReferenceDrawing,
              files: fileInputs,
            },
          },
        });

        const presignedUrls = data?.preSignedUrlToUploadFiles ?? [];

        for (const presignedUrl of presignedUrls) {
          const imageIndex = fileInputs.findIndex((file) => file.uuid === presignedUrl.uuid);
          const image = images[imageIndex];
          if (!image) continue;

          const uploadResponse = await fetch(presignedUrl.url, {
            method: 'PUT',
            headers: { 'Content-Type': 'image/png' },
            body: image.file,
          });

          if (uploadResponse.status === 200) {
            await updateFileStatus({
              variables: {
                input: {
                  referenceId: mapping.assemblyId,
                  collectionName: 'assembly',
                  fileUpdates: [
                    { fileId: presignedUrl.id, uploadStatus: FileUploadStatus.Success },
                  ],
                },
              },
              refetchQueries: [GetAssemblyAttachmentsDocument, GetAssemblyDetailsDocument],
            });
          }
        }
      } catch {
        hasError = true;
        showSnackBar(`Failed to upload ${mapping.file.name}`, 'error');
      }

      completed += 1;
      setProgress(Math.round((completed / total) * 100));
    }

    setIsUploading(false);

    if (!hasError) {
      showSnackBar('Reference drawings uploaded successfully', 'success');
      setIsOpen(false);
    }
  };

  return (
    <Stack gap="2.4rem" height="100%" width="120rem">
      <Stack gap="0.4rem">
        <Typography variant="body1" color="primary.600" fontWeight={500}>
          Review and upload
        </Typography>
        <Typography variant="body2" color="primary.400">
          {fileMappings.length} file{fileMappings.length !== 1 ? 's' : ''} will be uploaded as
          reference drawings.
        </Typography>
      </Stack>

      <Stack sx={{ overflowY: 'auto', flex: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, backgroundColor: abyssColors.primary[50] }}>
                File
              </TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: abyssColors.primary[50] }}>
                Assembly tag
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fileMappings.map((mapping, index) => (
              <TableRow key={`${mapping.file.name}-${index}`}>
                <TableCell>
                  <Typography variant="body2" color="primary.700">
                    {mapping.file.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="primary.700">
                    {mapping.tagName}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Stack>

      {isUploading && (
        <Stack gap="0.8rem">
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="caption" color="primary.400" textAlign="center">
            Uploading... {progress}%
          </Typography>
        </Stack>
      )}

      <Stack direction="row" justifyContent="space-between" marginTop="auto">
        <Button variant="outlined" onClick={goToPreviousStep} disabled={isUploading}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={isUploading}
          startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isUploading ? 'Uploading...' : 'Upload'}
        </Button>
      </Stack>
    </Stack>
  );
};
